/**
 * Renewal Forecast (Fix 2) — every account with <=5 sessions left or any
 * outstanding IOU, with projected zero date + weekly usage. CSV export.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Loader2, AlertTriangle } from "lucide-react";
import OpsTabs from "@/components/coach/OpsTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);

export default function RenewalForecast() {
    const [rows, setRows] = useState(null);
    const [busy, setBusy] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await axios.get(`${API}/coach/renewal-forecast`, { withCredentials: true });
                setRows(r.data?.accounts || []);
            } catch (e) {
                toast.error("Couldn't load forecast", { description: e?.response?.data?.detail });
                setRows([]);
            } finally {
                setBusy(false);
            }
        })();
    }, []);

    const downloadCsv = () => {
        window.open(`${API}/coach/renewal-forecast.csv`, "_blank", "noopener");
    };

    const dueNow = useMemo(
        () => (rows || []).filter((r) => r.projected_zero_date === "due now").length,
        [rows],
    );

    return (
        <div data-testid="renewal-forecast" className="min-h-screen bg-black text-white">
            <div className="max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-16">
                <OpsTabs />
                <Link to="/coach" className="inline-flex items-center gap-1.5 text-zinc-300 hover:text-white text-xs tracking-[0.18em] uppercase">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to coach
                </Link>
                <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Renewal Forecast</h1>
                        <p className="text-zinc-400 text-sm mt-1.5">
                            Accounts with 5 or fewer sessions left or any outstanding IOU.
                            {rows && <> <span className="text-amber-300">{dueNow} due now</span> · {rows.length} accounts.</>}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={downloadCsv}
                        data-testid="forecast-export-csv"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-zinc-700 text-zinc-200 hover:border-brand-red hover:text-brand-red text-[11px] tracking-[0.2em] uppercase transition-colors"
                    >
                        <Download className="w-3.5 h-3.5" /> Export CSV
                    </button>
                </div>

                {busy ? (
                    <div className="mt-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>
                ) : rows.length === 0 ? (
                    <p className="mt-12 text-center text-zinc-500 text-sm">No accounts need renewal right now. 🎉</p>
                ) : (
                    <div className="mt-8 overflow-x-auto border border-zinc-900 rounded-xl">
                        <table className="w-full text-sm" data-testid="forecast-table">
                            <thead>
                                <tr className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 border-b border-zinc-800">
                                    <th className="text-left px-4 py-3">Account</th>
                                    <th className="text-left px-4 py-3">Athletes</th>
                                    <th className="text-right px-4 py-3">Left</th>
                                    <th className="text-right px-4 py-3">IOUs</th>
                                    <th className="text-right px-4 py-3">/week</th>
                                    <th className="text-left px-4 py-3">Last pack</th>
                                    <th className="text-left px-4 py-3">Expiry</th>
                                    <th className="text-left px-4 py-3">Projected zero</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const due = r.projected_zero_date === "due now";
                                    return (
                                        <tr key={r.account_user_id} data-testid={`forecast-row-${r.account_user_id}`} className="border-b border-zinc-900/70 hover:bg-zinc-950/50">
                                            <td className="px-4 py-3 text-white">{r.account_holder}</td>
                                            <td className="px-4 py-3 text-zinc-400">{r.athletes}</td>
                                            <td className="px-4 py-3 text-right font-mono">{r.sessions_left}</td>
                                            <td className={`px-4 py-3 text-right font-mono ${r.outstanding_ious > 0 ? "text-amber-300" : "text-zinc-500"}`}>{r.outstanding_ious}</td>
                                            <td className="px-4 py-3 text-right font-mono text-zinc-400">{r.sessions_per_week}</td>
                                            <td className="px-4 py-3 text-zinc-400">
                                                {r.last_pack_size ? `${r.last_pack_size}-pk · ${money(r.last_pack_price)} · ${r.last_pack_date || "—"}` : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-zinc-400">{r.pack_expiry || "—"}</td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1 ${due ? "text-amber-300 font-semibold" : "text-zinc-300"}`}>
                                                    {due && <AlertTriangle className="w-3.5 h-3.5" />}
                                                    {r.projected_zero_date}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
